<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        role="combobox"
        :aria-expanded="open"
        :disabled="disabled"
        class="w-full justify-between !bg-white hover:!bg-white"
      >
        <div v-if="modelValue" class="flex items-center gap-2">
          <Avatar class="w-5 h-5">
            <AvatarImage v-if="modelValue.avatar" :src="modelValue.avatar" :alt="modelValue.name" />
            <AvatarFallback class="text-xs">{{ getInitials(modelValue.name) }}</AvatarFallback>
          </Avatar>
          <span class="truncate">{{ modelValue.name }}</span>
        </div>
        <span v-else class="text-muted-foreground">{{ placeholder }}</span>
        <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-[320px] p-0" align="start">
      <Command v-model:search-term="searchQuery">
        <CommandInput :placeholder="`Type ${minChars}+ characters to search...`" />
        <CommandList>
          <!-- Loading state -->
          <CommandEmpty v-if="isLoading">
            <div class="flex items-center justify-center gap-2 py-2">
              <Loader2 class="w-4 h-4 animate-spin" />
              <span>Searching...</span>
            </div>
          </CommandEmpty>
          <!-- No results after search -->
          <CommandEmpty v-else-if="!isLoading && searchQuery.length >= minChars && userList.length === 0">
            No users found.
          </CommandEmpty>
          <!-- Need more characters -->
          <CommandEmpty v-else-if="searchQuery.length > 0 && searchQuery.length < minChars">
            Type at least {{ minChars }} characters to search...
          </CommandEmpty>
          <!-- Initial state -->
          <CommandEmpty v-else-if="searchQuery.length === 0"> Start typing to search users... </CommandEmpty>
          <!-- Results list -->
          <CommandGroup v-if="!isLoading && userList.length > 0">
            <CommandItem v-for="user in userList" :key="user.id" :value="user" @select="handleSelect(user)">
              <div class="flex items-center gap-2 w-full">
                <Avatar class="w-6 h-6">
                  <AvatarImage v-if="user.avatar" :src="user.avatar" :alt="user.name" />
                  <AvatarFallback class="text-xs">{{ getInitials(user.name) }}</AvatarFallback>
                </Avatar>
                <div class="flex flex-col flex-1 min-w-0">
                  <span class="text-sm font-medium truncate">{{ user.name }}</span>
                  <span v-if="user.email" class="text-xs text-muted-foreground truncate">{{ user.email }}</span>
                </div>
                <CheckIcon v-if="modelValue?.id === user.id" class="ml-auto w-4 h-4 shrink-0" />
              </div>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { CheckIcon } from '@radix-icons/vue'
import { ChevronsUpDown, Loader2 } from 'lucide-vue-next'
import { ref, watch } from 'vue'
import { useUserSearch } from '@/composables'
import { Avatar, AvatarFallback, AvatarImage } from '@/foundation/avatar'
import { Button } from '@/foundation/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/foundation/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/foundation/popover'

type User = {
  id: string
  name: string
  email?: string
  avatar?: string
}

type Props = {
  modelValue?: User | null
  placeholder?: string
  disabled?: boolean
  minChars?: number
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Search users...',
  disabled: false,
  minChars: 2,
})

const emit = defineEmits<{
  'update:modelValue': [value: User | null]
}>()

const open = ref(false)
const { searchQuery, userList, isLoading } = useUserSearch(props.minChars)

const getInitials = (name: string) => {
  if (!name) return '?'
  const parts = name.split(' ')

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }

  return name.slice(0, 2).toUpperCase()
}

const handleSelect = (user: User) => {
  emit('update:modelValue', user)
  open.value = false
  searchQuery.value = ''
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue) {
      searchQuery.value = ''
    }
  },
)
</script>
